/**
 * NowPlaying — track title, artist and listener count.
 *
 * The marquee for long titles is a CSS transform loop (framer-motion), so it
 * never touches layout width and can never force horizontal overflow — that is
 * what caused the erratic page overflow in v2 on narrow screens.
 *
 * v2 rendered the title as a bare <p>; v3 adds data-testid hooks
 * ("now-playing", "now-playing-artist", "listener-count") so the track and
 * artist bindings can be asserted and targeted without scraping class names.
 */

import React from 'react';
import { motion } from 'framer-motion';

/** Above this length the title scrolls instead of wrapping. v2 used 35. */
const MARQUEE_THRESHOLD = 35;

export default function NowPlaying({ title, artist, listeners, isOffline }) {
  const shouldScroll = title.length > MARQUEE_THRESHOLD;
  // Longer titles need a proportionally longer loop so the speed reads the same.
  const duration = Math.max(20, title.length * 0.5);

  return (
    <div className="w-full max-w-lg text-center">
      <p className="dmr-label mb-2">Now Playing</p>

      <div className="relative h-8 overflow-hidden">
        {shouldScroll ? (
          <motion.div
            className="dmr-marquee absolute"
            animate={{ x: ['100%', '-100%'] }}
            transition={{ duration, repeat: Infinity, ease: 'linear' }}
          >
            <span
              data-testid="now-playing"
              className="text-lg text-[#00FF41] md:text-xl"
            >
              {title}
            </span>
            <span className="mx-16 text-lg text-[#00FF41] md:text-xl" aria-hidden="true">
              •
            </span>
            <span className="text-lg text-[#00FF41] md:text-xl" aria-hidden="true">
              {title}
            </span>
          </motion.div>
        ) : (
          <p data-testid="now-playing" className="text-lg text-[#00FF41] md:text-xl">
            {title}
          </p>
        )}
      </div>

      {artist && artist !== 'DeadMoon Radio' && (
        <p data-testid="now-playing-artist" className="mt-1 text-xs text-[#404040]">
          {artist}
        </p>
      )}

      {listeners > 0 && (
        <p data-testid="listener-count" className="mt-2 text-xs text-[#404040]">
          {listeners} listener{listeners !== 1 ? 's' : ''}
        </p>
      )}

      {isOffline && (
        <p className="mt-2 text-xs text-[#ff5f56]/70">
          the transmitter is cold. nothing is coming through.
        </p>
      )}
    </div>
  );
}
